import { IsBoolean, IsIn, IsObject, IsOptional } from 'class-validator';
import type { FileStage } from './files.dto';

/**
 * By-law workflows — the wire vocabulary.
 *
 * Every by-law type a municipality enforces runs through the same file-level
 * pipeline (intake → review → site visit → classification → escalation →
 * closed). What differs between them — the steps inside each stage, the
 * statutory clocks, the fields and checklists an officer must complete, and the
 * notice and order letters — is *data*: a {@link WorkflowDefinition}, held in
 * the catalogue in `@munifocus/domain-stages`, resolved against the
 * municipality's jurisdiction and served by the API. A screen renders a
 * definition; it never forks on the by-law type.
 *
 * The Dangerous & Unsightly workflow is the exception that proves the rule: it
 * predates the catalogue and keeps its purpose-built screens and server gates
 * (`engine: 'legacy'`). Its definition describes it — the same stages and the
 * same statutory clocks — so the two can be compared, but nothing about how a
 * D&U file is worked changed when the catalogue arrived.
 */

/** Categories that run on the original, purpose-built D&U workflow. */
export const LEGACY_BYLAW_CATEGORIES = ['dangerous_unsightly', 'parking', 'other'] as const;

/** Categories whose workflow is entirely a catalogue definition. */
export const CONFIGURED_BYLAW_CATEGORIES = [
  'property_standards',
  'zoning',
  'noise',
  'animal_control',
  'short_term_rental',
] as const;

export const BYLAW_CATEGORIES = [
  ...LEGACY_BYLAW_CATEGORIES,
  ...CONFIGURED_BYLAW_CATEGORIES,
] as const;

export type LegacyBylawCategory = (typeof LEGACY_BYLAW_CATEGORIES)[number];
export type ConfiguredBylawCategory = (typeof CONFIGURED_BYLAW_CATEGORIES)[number];

const CONFIGURED_SET: ReadonlySet<string> = new Set(CONFIGURED_BYLAW_CATEGORIES);

/** True for a category whose workflow is driven by its catalogue definition. */
export function isConfiguredBylawCategory(
  category: string | null | undefined,
): category is ConfiguredBylawCategory {
  return !!category && CONFIGURED_SET.has(category);
}

/** One string in both official languages. Data, so it carries both. */
export interface Bilingual {
  en: string;
  fr: string;
}

/** The file-level stages a configured step can belong to. Intake is shared. */
export const CONFIGURED_WORKFLOW_STAGES = [
  'review',
  'site_visit',
  'classification',
  'escalation',
] as const satisfies readonly FileStage[];
export type ConfiguredWorkflowStage = (typeof CONFIGURED_WORKFLOW_STAGES)[number];

export const WORKFLOW_FIELD_TYPES = [
  'text',
  'textarea',
  'date',
  'boolean',
  'select',
  'number',
] as const;
export type WorkflowFieldType = (typeof WORKFLOW_FIELD_TYPES)[number];

export interface WorkflowFieldOption {
  value: string;
  label: Bilingual;
}

/** One thing an officer records at a step. */
export interface WorkflowField {
  key: string;
  type: WorkflowFieldType;
  label: Bilingual;
  help?: Bilingual;
  /** Must be filled before the step counts as complete. */
  required: boolean;
  /** For `select`. */
  options?: WorkflowFieldOption[];
}

/** One line of a step's checklist — ticked, not typed. */
export interface WorkflowChecklistItem {
  key: string;
  label: Bilingual;
  required: boolean;
  /** The statute this item answers to, cited beside it. */
  statuteKey?: string;
}

export const WORKFLOW_CONDITION_OPS = ['set', 'unset', 'true', 'false', 'eq', 'neq'] as const;
export type WorkflowConditionOp = (typeof WORKFLOW_CONDITION_OPS)[number];

/** A test on one recorded value: `step.field op value`. */
export interface WorkflowCondition {
  step: string;
  field: string;
  op: WorkflowConditionOp;
  /** For `eq` / `neq`. */
  value?: string;
}

/** One unit of recorded work inside a stage. */
export interface WorkflowStep {
  key: string;
  stage: ConfiguredWorkflowStage;
  title: Bilingual;
  description: Bilingual;
  fields: WorkflowField[];
  checklist: WorkflowChecklistItem[];
  /** Steps that must be complete before this one can be recorded. */
  requires: string[];
  /**
   * Saving this step with any one of these true may close the file — the
   * terminal decisions of the escalation path (compliance achieved, order
   * withdrawn, proceedings concluded).
   */
  closesWhen: WorkflowCondition[];
  /** Template keys offered from this step. */
  documents: string[];
}

/**
 * Where a classification sends a file. The step's `field` is read; a value in
 * `close` closes the file at classification, anything else escalates.
 */
export interface WorkflowClassificationRoute {
  step: string;
  field: string;
  close: string[];
}

export type WorkflowDeadlineUnit = 'days' | 'months';

/**
 * A statutory clock, described rather than coded: it starts on a recorded
 * date, runs for a length, is live only while `activeWhen` all hold, and stops
 * once any `stopWhen` holds.
 */
export interface WorkflowDeadlineRule {
  key: string;
  label: Bilingual;
  statuteKey: string;
  start: { step: string; field: string };
  unit: WorkflowDeadlineUnit;
  length: number;
  /**
   * Whether a municipality may set its own length. Compliance periods are a
   * by-law's own choice; a limitation period fixed by statute is not.
   */
  adjustable: boolean;
  activeWhen: WorkflowCondition[];
  stopWhen: WorkflowCondition[];
}

export const WORKFLOW_DOCUMENT_KINDS = ['notice', 'order', 'letter'] as const;
export type WorkflowDocumentKind = (typeof WORKFLOW_DOCUMENT_KINDS)[number];

/**
 * A letter the workflow produces, in both languages. `body` holds
 * `{{placeholder}}` merge fields — see `WORKFLOW_TEMPLATE_PLACEHOLDERS` in
 * `@munifocus/domain-stages` for the vocabulary.
 */
export interface WorkflowDocumentTemplate {
  key: string;
  kind: WorkflowDocumentKind;
  title: Bilingual;
  body: Bilingual;
  statuteKeys: string[];
}

/** A statute or by-law the workflow cites, by key. The citation is per jurisdiction. */
export interface WorkflowStatute {
  key: string;
  label: Bilingual;
}

export interface WorkflowDefinition {
  category: (typeof BYLAW_CATEGORIES)[number];
  /** `legacy` runs on the purpose-built D&U screens; `configured` on the definition. */
  engine: 'legacy' | 'configured';
  /** Bumped whenever the definition changes in a way a stored file could notice. */
  version: number;
  label: Bilingual;
  summary: Bilingual;
  stages: FileStage[];
  steps: WorkflowStep[];
  classification: WorkflowClassificationRoute | null;
  deadlines: WorkflowDeadlineRule[];
  documents: WorkflowDocumentTemplate[];
  statutes: WorkflowStatute[];
}

/**
 * A definition as one municipality sees it: its statute citations filled in
 * from the jurisdiction (and any local override), and its adjustable clocks at
 * the municipality's own length.
 */
export interface ResolvedWorkflowDefinition extends WorkflowDefinition {
  /** statuteKey → citation. Empty string when nobody has configured one. */
  citations: Record<string, string>;
}

/** A province or territory the product knows the statutes of. */
export interface JurisdictionSummary {
  code: string;
  name: Bilingual;
  timeZone: string;
}

/** `GET /local-governments/:id/bylaw-workflows` — what officers there may open. */
export interface LocalGovernmentWorkflows {
  localGovernmentId: string;
  /** The module flag. Off means only the original categories are offered. */
  multiBylawEnabled: boolean;
  jurisdiction: JurisdictionSummary;
  /** Every category an officer may open a file under here, in display order. */
  available: ResolvedWorkflowDefinition[];
  /** Every statute the available workflows cite, for the citations editor. */
  statutes: WorkflowCitationRow[];
}

/** One statute's citation at a municipality: the jurisdiction's, and its own. */
export interface WorkflowCitationRow {
  key: string;
  label: Bilingual;
  /** What the jurisdiction cites. Empty when it has none. */
  jurisdictionDefault: string;
  /** The municipality's own, or null to use the default. */
  override: string | null;
}

/** One step's standing on a file. */
export interface WorkflowStepState {
  key: string;
  complete: boolean;
  /** Field and checklist keys still required. */
  missing: string[];
  /** A required earlier step is not complete yet. */
  locked: boolean;
}

/** One live configured clock on a file. */
export interface WorkflowDeadlineState {
  key: string;
  label: Bilingual;
  statuteRef: string;
  deadlineIso: string;
  daysRemaining: number;
  expired: boolean;
}

/** What one step has recorded: its field values, and its ticked checklist. */
export interface WorkflowStepRecord {
  values: Record<string, string | number | boolean | null>;
  checklist: Record<string, boolean>;
  updatedAt?: string;
}

/** `GET /files/:id/bylaw-workflow`. */
export interface FileWorkflowState {
  fileId: string;
  stage: FileStage;
  definition: ResolvedWorkflowDefinition;
  records: Record<string, WorkflowStepRecord>;
  steps: WorkflowStepState[];
  deadlines: WorkflowDeadlineState[];
  /** The current stage's steps are all complete. */
  canAdvance: boolean;
  /** At escalation: a recorded decision allows the file to close. */
  canClose: boolean;
}

/** `PUT /files/:id/bylaw-workflow/steps/:stepKey`. Typed against the definition server-side. */
export class SaveWorkflowStepDto {
  @IsObject()
  values!: Record<string, string | number | boolean | null>;

  @IsOptional()
  @IsObject()
  checklist?: Record<string, boolean>;

  /** Optimistic guard — refused if the file has moved on. */
  @IsOptional()
  @IsIn(['intake', 'review', 'site_visit', 'classification', 'escalation', 'closed'])
  expectedStage?: FileStage;
}

/** `POST /files/:id/bylaw-workflow/advance`. */
export class AdvanceWorkflowDto {
  @IsOptional()
  @IsIn(['intake', 'review', 'site_visit', 'classification', 'escalation', 'closed'])
  expectedStage?: FileStage;

  /** At escalation, close the file (needs a recorded closing decision). */
  @IsOptional()
  @IsBoolean()
  close?: boolean;
}

export const WORKFLOW_DOCUMENT_LOCALES = ['bilingual', 'en', 'fr'] as const;
export type WorkflowDocumentLocale = (typeof WORKFLOW_DOCUMENT_LOCALES)[number];

/** `POST /files/:id/bylaw-workflow/documents/:templateKey`. */
export class GenerateWorkflowDocumentDto {
  @IsOptional()
  @IsIn(WORKFLOW_DOCUMENT_LOCALES)
  locale?: WorkflowDocumentLocale;
}

/** A citation as typed: short, single line. */
export const CITATION_MAX_LENGTH = 300;

/**
 * `PUT /local-governments/:id/bylaw-workflows/citations` — the municipality's
 * own statute and by-law citations (by-law numbers are a municipal fact).
 * Keys outside the catalogue are refused server-side; an empty string clears an
 * override back to the jurisdiction default.
 */
export class UpdateWorkflowCitationsDto {
  @IsObject()
  citations!: Record<string, string>;
}

/** Statute keys look like `lga.appeal` or `bylaw.noise`. */
export const STATUTE_KEY_PATTERN = /^[a-z][a-z0-9_]*(\.[a-z0-9_]+)*$/;

/** Workflow refusals a client can translate (`gov.bylawWorkflow.errors.<code>`). */
export type BylawWorkflowErrorCode =
  /** The by-law type is not enabled at the file's municipality. */
  | 'bylaw_type_unavailable'
  /** The file runs on its definition; the D&U stage routes do not apply. */
  | 'configured_workflow'
  /** The file runs on the original D&U workflow; the configured routes do not apply. */
  | 'legacy_workflow'
  /** No such step / template in the file's definition. */
  | 'unknown_step'
  /** A step this one requires is not complete. */
  | 'step_locked'
  /** A value does not fit its field. */
  | 'invalid_value'
  /** The current stage still has required work outstanding. */
  | 'requirements_missing'
  /** Closing from escalation needs a recorded closing decision. */
  | 'closure_not_reached';

/** A recorded value is "set" when it is neither absent, null nor blank. */
export function isWorkflowValueSet(value: unknown): boolean {
  return value !== undefined && value !== null && !(typeof value === 'string' && !value.trim());
}

/**
 * Whether one condition holds against a file's recorded steps. The one reading
 * of a condition — the server's gates, the deadline derivation and any screen
 * that previews them all call this, so they cannot disagree.
 */
export function workflowConditionHolds(
  records: Readonly<Record<string, { values?: Record<string, unknown> } | undefined>>,
  condition: WorkflowCondition,
): boolean {
  const value = records[condition.step]?.values?.[condition.field];
  switch (condition.op) {
    case 'set':
      return isWorkflowValueSet(value);
    case 'unset':
      return !isWorkflowValueSet(value);
    case 'true':
      return value === true;
    case 'false':
      return value === false;
    case 'eq':
      return isWorkflowValueSet(value) && String(value) === condition.value;
    case 'neq':
      return isWorkflowValueSet(value) && String(value) !== condition.value;
  }
}
